// Shared money/date rendering — every screen shows numbers through these two
// so a figure is formatted the same way everywhere (DESIGN_INSTRUCTIONS.md §1/§5).

/**
 * Format an amount as rupees, from EITHER shape the API sends.
 *
 * Most routes send a plain number of rupees. Several — service quotes, accepted prices, offer
 * discounts — send a money OBJECT, `{minor_units, currency, display}`, where `minor_units` is
 * paise. `Number({...})` is `NaN` and `NaN || 0` is `0`, so passing an object here used to render
 * a confident **"₹0"**: not an error, not a warning, just a wrong number that looks deliberate.
 *
 * Handled here rather than at each call site because there are twenty-odd call sites and one
 * function. A caller cannot forget what it never has to remember.
 */
export function rupees(value) {
  const isMoneyObject =
    value && typeof value === "object" && typeof value.minor_units === "number";
  // DEV-ONLY tripwire. The canonical shape is `{ minor_units: X }`; a bare number is treated as
  // rupees, so a paise figure would render 100× too large and a wrong/absent field renders ₹0.
  // The real guard is the STATIC `no-restricted-syntax` rule on rupees() (craco.config.js) — this
  // only surfaces a dynamically-constructed slip in local/dev runs. Never throws: a display bug
  // must not become a crash in production.
  if (
    !isMoneyObject &&
    process.env.NODE_ENV !== "production" &&
    value !== null &&
    value !== undefined &&
    value !== ""
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `rupees() called with a non-object value (${JSON.stringify(value)}). Pass { minor_units: X } — ` +
        "a bare number is read as rupees (paise would render 100× too large). See lib/format.js.",
    );
  }
  // RF-7 (C2): this used to be `maximumFractionDigits: 0`, which rounded ₹1,234.50 to ₹1,235 on the
  // Record-sale chip, Bills list and khata banner. Whole-rupee values still print without ".00";
  // paise appear only when present, with en-IN lakh/thousand grouping either way.
  return rupeesSmart(isMoneyObject ? value.minor_units : Math.round((Number(value) || 0) * 100));
}

/**
 * Whole rupees when there are no paise ("₹1,235"), two decimals only when there are ("₹1,234.50").
 * This is what `rupees()` now delegates to (RF-7) — a figure that can carry paise must not silently
 * round (₹1234.5 showing as ₹1,235). Takes MINOR units (paise).
 */
export function rupeesSmart(minor) {
  const paise = Math.round(Number(minor) || 0);
  const hasPaise = paise % 100 !== 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  }).format(fromMinor(paise));
}

/**
 * MONEY UNITS — the one place the web converts between rupees and paise.
 *
 * The API speaks integer MINOR units (paise): `12000` is ₹120. Nothing outside this file divides or
 * multiplies by 100 (`lib/__tests__/moneyUnits.guard.test.js` fails the build if it does):
 *
 *  - DISPLAY: `rupees({ minor_units })` above (whole rupees), or `rupeesExact(minor)` when the paise
 *    matter (a sale total, a bill share message).
 *  - INPUT: `toMinor(rupeesTyped)` -> paise for the request body; `toRupeeInput(minor)` -> the
 *    "120.00" string that pre-fills a rupee text input.
 *  - `fromMinor(minor)` -> a rupees NUMBER, for the places that need one (chart series, a UPI `am=`).
 *  - `bpsToPercent` / `percentToBps` / `ratioToPercent` — basis points and ratios, same trap.
 */
export const toMinor = (rupeesInput) => Math.round((Number(rupeesInput) || 0) * 100);

export const fromMinor = (minor) => (Number(minor) || 0) / 100;

export const toRupeeInput = (minor) => fromMinor(minor).toFixed(2);

// Always two decimals (a sale total, a bill share message), thousands-grouped (RF-7/U6): the
// ungrouped `toFixed` form printed "₹1234.50".
export const rupeesExact = (minor) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(fromMinor(minor));

export const bpsToPercent = (bps) => (Number(bps) || 0) / 100;

export const percentToBps = (percent) => Math.round((Number(percent) || 0) * 100);

/** A machine slug as readable words, for the LAST-RESORT case where a label map has no entry:
 * "bench_press" -> "Bench press", "platform_admin" -> "Platform admin". Never the first choice -
 * a known value should go through its label map (and be translated) - but a raw slug on screen is
 * worse than a plain-English one. */
export function humanizeSlug(value) {
  const words = String(value ?? "")
    .replace(/[_-]+/g, " ")
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

/** R5-11: a task's urgency, kept honest as time passes. "Today" / "Right now" are relative to when
 * the request was filed, so on a later day they are wrong - show the filing date instead
 * ("Needed on 31 Oct"). Same-day and "this week" read as before. */
export function urgencyLabel(t, lang, urgency, createdAt) {
  if (!urgency) return "";
  if ((urgency === "today" || urgency === "now") && createdAt && businessDay(createdAt) !== businessDay()) {
    return t("tasks.urgencyOn", { date: shortDate(createdAt, lang) });
  }
  return t(`tasks.urgencyLabels.${urgency}`);
}

/** A worker's trade as a label. The trade is stored as a lowercase slug ("plumber"); a Hindi
 * `trade_hi` is shown as typed, the English one is capitalised. Mirrors mobile `tradeLabel`. */
export function tradeLabel(lang, record) {
  if (!record) return "";
  if (lang === "hi" && record.trade_hi) return record.trade_hi;
  return humanizeSlug(record.trade);
}

/** Label for a server enum/slug value: the translated `{prefix}.{value}` entry, else a humanised
 * slug when the label map has no entry (a new value must show readable text, never "app_popup" or
 * the dotted key). `t` is the app's translate function. */
export function enumLabel(t, prefix, value) {
  if (value === null || value === undefined || value === "") return "";
  const key = `${prefix}.${value}`;
  const label = t(key);
  return typeof label === "string" && label !== key ? label : humanizeSlug(value);
}

/** R7-7: the owner/city-desk display category in the UI language: the Hindi label
 * (`display_category_hi`) on a Hindi screen, falling back to the English one when it is blank. */
export function displayCategoryText(business, lang) {
  const en = (business?.display_category || "").trim();
  const hi = (business?.display_category_hi || "").trim();
  return (lang === "hi" && hi) || en;
}

/** R6-7: the label for a business's kind. The city-desk-editable `display_category` wins when set
 * (a banquet hall stored under the `hotel` preset reads "Banquet hall"); blank falls back to the
 * translated kind label. */
export function businessKindLabel(t, business, lang) {
  const custom = displayCategoryText(business, lang);
  return custom || enumLabel(t, "storefront.businessTypes", business?.business_type);
}

/** The bill total in words for the UI language: the server's Hindi string on a Hindi screen,
 * falling back to English when it is empty or the language is not Hindi. Both strings come from
 * the API (`amount_in_words`, `amount_in_words_hi`), never composed here. */
export const amountInWords = (source, lang) =>
  (lang === "hi" && source?.amount_in_words_hi) || source?.amount_in_words || "";

/** A 0..1 ratio (or a delta over a base) as a whole percentage. */
export const ratioToPercent = (ratio) => Math.round((Number(ratio) || 0) * 100);

const HOURS_RANGE = /^(\d{1,2}):(\d{2})\s*(am|pm)\s*-\s*(\d{1,2}):(\d{2})\s*(am|pm)$/i;

function toTodayDate(hour12, minute, meridiem) {
  let h = hour12 % 12;
  if (meridiem.toLowerCase() === "pm") h += 12;
  const d = new Date();
  d.setHours(h, minute, 0, 0);
  return d;
}

/** Best-effort "open now" / "opens at H" parse from a free-text hours string like
 * "9:00 AM - 9:00 PM" or "Open 24 hours" (City Home v2, design_handoff_website_v2 §4). Returns
 * null for anything else — never a wrong time from a shape we don't actually understand, and
 * doesn't attempt overnight ranges (close time earlier than open time). */
export function parseOpenStatus(hours, lang) {
  if (!hours) return null;
  if (/open 24 hours?/i.test(hours)) return { isOpen: true };
  const m = hours.match(HOURS_RANGE);
  if (!m) return null;
  const opens = toTodayDate(Number(m[1]), Number(m[2]), m[3]);
  const closes = toTodayDate(Number(m[4]), Number(m[5]), m[6]);
  if (closes <= opens) return null; // overnight ranges: not handled, don't guess
  const now = new Date();
  if (now >= opens && now < closes) return { isOpen: true };
  const locale = lang === "hi" ? "hi-IN" : "en-IN";
  const opensLabel = hiMeridiem(
    new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(opens),
    lang,
    opens.getHours(),
  );
  return { isOpen: false, opensLabel };
}

// R6-9: ICU `hi-IN` emits Latin "am"/"pm". Hindi reads time of day BEFORE the clock time and "बजे"
// after it ("शाम 6:00 बजे"), so the day-period token is replaced by a word chosen from the IST hour:
// सुबह 05:00-11:59, दोपहर 12:00-15:59, शाम 16:00-18:59, रात 19:00-04:59.
export function hiTimeOfDay(hour) {
  if (hour >= 5 && hour < 12) return "सुबह";
  if (hour >= 12 && hour < 16) return "दोपहर";
  if (hour >= 16 && hour < 19) return "शाम";
  return "रात";
}
const IST_OFFSET_MIN = 330;
export function istHour(date) {
  return Math.floor(((date.getUTCHours() * 60 + date.getUTCMinutes() + IST_OFFSET_MIN) % 1440) / 60);
}
export function hiMeridiem(text, lang, hour) {
  if (lang !== "hi" || typeof text !== "string") return text;
  return text.replace(/(\d{1,2}:\d{2})\s*(?:am|pm)\b/i, (_m, clock) => `${hiTimeOfDay(hour)} ${clock} बजे`);
}

// Users are in IST. Rendering a datetime in the browser's zone (or, worse, raw UTC with a "Z")
// shows the wrong wall-clock time; pinning the zone here keeps every screen honest regardless of
// the device, and keeps a date-only field on the right side of a midnight boundary.
const IST = "Asia/Kolkata";

export function shortDate(isoString, lang) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "hi" ? "hi-IN" : "en-IN";
  try {
    return new Intl.DateTimeFormat(locale, { timeZone: IST, day: "numeric", month: "short" }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-IN", { timeZone: IST, day: "numeric", month: "short" }).format(date);
  }
}

// Time-of-day only, for slot chips (table reservation / doctor appointment pickers). Same IST
// pinning as the rest of this file — a raw `start_at.slice(11)` showed UTC with a literal "Z"
// (see 04_fix_log.md, Storefront S1, A1); this renders e.g. "6:00 PM" instead.
export function localTime(isoString, lang) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "hi" ? "hi-IN" : "en-IN";
  const opts = { timeZone: IST, hour: "numeric", minute: "2-digit" };
  try {
    return hiMeridiem(new Intl.DateTimeFormat(locale, opts).format(date), lang, istHour(date));
  } catch {
    return new Intl.DateTimeFormat("en-IN", opts).format(date);
  }
}

// A calendar date WITH its year ("3 Nov 2026"), no time. `shortDate` has no year and `dateTime`
// carries a time; this is the one for a date that can sit in a different year from today - a
// verified-on date, an event's dates, the next bill. Also right for DATE-only fields ("2026-11-03"),
// which parse as UTC midnight and so land on the same IST day.
export function longDate(isoString, lang) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "hi" ? "hi-IN" : "en-IN";
  const opts = { timeZone: IST, day: "numeric", month: "short", year: "numeric" };
  try {
    return new Intl.DateTimeFormat(locale, opts).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-IN", opts).format(date);
  }
}

// R3-14: server refusals embed raw ISO dates ("...it starts on 2026-11-19."). Rewrite each
// standalone YYYY-MM-DD in a message to the app's readable date ("19 Nov 2026"); anything that is
// not a valid calendar date is left exactly as it was.
export function humanizeIsoDates(text, lang) {
  if (typeof text !== "string") return text;
  return text.replace(/\b(\d{4})-(\d{2})-(\d{2})\b(?!T)/g, (match) => longDate(match, lang) || match);
}

// Date + time, for the slot a real appointment or table reservation is booked into. Same shape and
// same guards as `shortDate` — one date style, not two — but with the time and an explicit year.
export function dateTime(isoString, lang) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "hi" ? "hi-IN" : "en-IN";
  const opts = { timeZone: IST, day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" };
  try {
    return hiMeridiem(new Intl.DateTimeFormat(locale, opts).format(date), lang, istHour(date));
  } catch {
    return new Intl.DateTimeFormat("en-IN", opts).format(date);
  }
}

// R5-5: a slot as a person says it - "2 Nov, 7:00 am" - in IST. No year (a slot is always close),
// unlike `dateTime`. Replaces raw `slot_start_at.replace("T", " ")`, which showed UTC with a "Z".
export function dayTime(isoString, lang) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "hi" ? "hi-IN" : "en-IN";
  const opts = { timeZone: IST, day: "numeric", month: "short", hour: "numeric", minute: "2-digit" };
  try {
    return hiMeridiem(new Intl.DateTimeFormat(locale, opts).format(date), lang, istHour(date));
  } catch {
    return new Intl.DateTimeFormat("en-IN", opts).format(date);
  }
}

// There is no order-number column — every "#…" an owner sees is a truncation of the raw UUID, done
// client-side. Before this helper, three screens each truncated differently (`RequestsPanel`,
// `MyActivity`: last 6 chars of the dashed string; `TodayPanel`'s old `shortRef`: dashes stripped,
// last 4, upper-cased) so the SAME order showed "#b85605" in one place and "#5605" in another
// (W-07). One scheme, used everywhere: strip dashes, last 6 hex chars, upper-cased.
export function shortOrderRef(id) {
  if (!id) return "";
  return String(id).replace(/-/g, "").slice(-6).toUpperCase();
}

/** R4-10: an E.164 Indian number grouped for reading - "+919000000001" -> "+91 90000 00001". Anything
 * that is not a +91 ten-digit number is returned untouched (never guessed at). */
export function spacedPhone(phone) {
  const m = /^(?:\+91)?(\d{5})(\d{5})$/.exec(String(phone || ""));
  return m ? `+91 ${m[1]} ${m[2]}` : phone || "";
}

/** The account page's masked mobile: "+919876542393" -> "+91 ••••• •2393" (last four only). Anything
 * that is not a +91 ten-digit number is masked by `maskPhone` instead - never shown in full. */
export function maskedMobile(phone) {
  const m = /^\+91\d{6}(\d{4})$/.exec(String(phone || ""));
  return m ? `+91 ••••• •${m[1]}` : maskPhone(phone);
}

/** A phone number that is safe to show in a header or greeting: `•••• 3210`, never the full number.
 * Mirrors the backend's `•••{last4}` seat mask (`directory/routes.py::list_members`). */
export function maskPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length >= 4 ? `•••• ${digits.slice(-4)}` : "";
}

/** The name a signed-in person is shown under (S7): their display name, else a MASKED phone. */
export function identityLabel(identity) {
  return identity?.display_name || maskPhone(identity?.phone_e164) || "";
}

// ---------------------------------------------------------------------------------------------
// THE business date. A date-only value ("YYYY-MM-DD": a due date, an expiry, an attendance day, a
// booking day) is the IST calendar day - same rule as the backend's `core/time.py business_day()`.
// Never derive it from `new Date().toISOString().slice(0, 10)` (that is the UTC day: before 05:30
// IST it is yesterday) nor from the device-local day (wrong off-IST). IST has no DST, so a fixed
// +05:30 offset is exact and needs no Intl/timezone-data support. scripts/check_business_dates.py
// fails the gate on the raw patterns; this file is the only allowlisted home of the arithmetic.
const IST_OFFSET_MS = 330 * 60 * 1000;

/** The IST calendar day an instant falls on, as "YYYY-MM-DD". Defaults to now. */
export function businessDay(instant = new Date()) {
  return new Date(new Date(instant).getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/** Calendar arithmetic on a business date: `addDays("2026-10-31", 1) === "2026-11-01"`. */
export function addDays(day, n) {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Days in the current IST business month (for run-rate projections). */
export function daysInBusinessMonth(day = businessDay()) {
  const [y, m] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** A month-end projection from fewer days than this is noise (one day of stay revenue x 30 read
 * "On pace for ₹2,31,000" on the 1st), so no pace is shown until a week of the window has passed. */
export const MIN_PACE_DAYS = 7;

/**
 * The ONE run-rate projection ("On pace for ₹X this month") for every owner/city chart. Returns
 * paise, or null when there is nothing honest to project: no revenue, a non-finite figure, or a
 * window shorter than MIN_PACE_DAYS.
 */
export function projectMonthMinor({ revenueMinor, daysElapsed, day = businessDay() }) {
  if (!Number.isFinite(revenueMinor) || revenueMinor <= 0) return null;
  if (!Number.isFinite(daysElapsed) || daysElapsed < MIN_PACE_DAYS) return null;
  return Math.round((revenueMinor / daysElapsed) * daysInBusinessMonth(day));
}

/**
 * Privacy (APP_RULES section 2: no raw internal value on screen). Masks every phone-like run of 10+
 * digits (with or without +91 / spaces) to its last four ("****0222") and drops bare UUIDs. The ONE
 * scrubber for text that comes from an audit/free-form field, so a row that carries a phone today or
 * tomorrow cannot render it whole. Stored data is untouched - this is render-only.
 */
export function maskPhones(text) {
  return String(text ?? "")
    .replace(/\+91[\s-]\d{5}[\s-]?\d{5}/g, (m) => `****${m.replace(/\D/g, "").slice(-4)}`)
    .replace(/\+?\d{10,}/g, (m) => `****${m.slice(-4)}`)
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
